/**
 * Dev tools show in local dev, or on a non-production deploy built with NEXT_PUBLIC_ENABLE_DEVTOOLS=true. Never in
 * production, where the mock routes answer 404 anyway.
 */
export const DEV_TOOLS_ENABLED =
  process.env.NEXT_PUBLIC_ENV !== 'production' &&
  (process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_ENABLE_DEVTOOLS === 'true');

// Read by the @repo/*-api wrappers, which forward the config cookie to the mock routes as this header
export const FORCE_MOCK_COOKIE = 'FORCE_MOCK';
export const MOCK_CONFIG_COOKIE = 'MOCK_CONFIG';
export const MOCK_CONFIG_HEADER = 'x-mock-config';

// Signed, httpOnly cookie that lets a deployed browser see the dev tools and reach the mock routes. The api wrappers
// forward it to the mock routes as this header, since their server-side fetch carries no cookies
export const DEV_TOOLS_UNLOCK_COOKIE = 'DEV_TOOLS_UNLOCK';
export const DEV_TOOLS_UNLOCK_HEADER = 'x-dev-tools-unlock';
// Seconds an unlock lasts
export const DEV_TOOLS_UNLOCK_MAX_AGE = 7 * 24 * 60 * 60;

const encoder = new TextEncoder();

const toBase64Url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

// Web Crypto, so the proxy can check it too
const hmac = async (secret: string, payload: string) => {
  const key = await crypto.subtle.importKey('raw', encoder.encode(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign',
  ]);
  return toBase64Url(await crypto.subtle.sign('HMAC', key, encoder.encode(payload)));
};

/** DEV_TOOLS_SECRET without the spaces or line break a pasted env value may carry; undefined when blank. */
export const devToolsSecret = () => process.env.DEV_TOOLS_SECRET?.trim() || undefined;

/** Compares in time independent of where the texts differ, so guesses can't be timed. */
export const sameText = (a: string, b: string) =>
  a.length === b.length && [...a].reduce((diff, char, i) => diff | (char.charCodeAt(0) ^ b.charCodeAt(i)), 0) === 0;

/**
 * Unlock cookie value: its expiry and that expiry signed with the secret, so rotating the secret locks every browser.
 *
 * @param {string} secret - DEV_TOOLS_SECRET.
 * @param {number} now - Current time in ms.
 */
export const signDevToolsUnlock = async (secret: string, now = Date.now()) => {
  const expires = `${now + DEV_TOOLS_UNLOCK_MAX_AGE * 1000}`;
  return `${expires}.${await hmac(secret, expires)}`;
};

/**
 * Whether an unlock cookie value is unexpired and signed with the secret.
 *
 * @param {string | null} value - Cookie or header value, as written by signDevToolsUnlock.
 * @param {string} secret - DEV_TOOLS_SECRET; without it nothing unlocks.
 * @param {number} now - Current time in ms.
 */
export const verifyDevToolsUnlock = async (value: string | null | undefined, secret?: string, now = Date.now()) => {
  if (!value || !secret) return false;
  const [expires, signature] = value.split('.');
  if (!signature || !(Number(expires) > now)) return false;
  return sameText(signature, await hmac(secret, expires!));
};

/**
 * Whether a request may use the dev tools and the mock routes: never in production, always in local dev, and on a
 * deploy only with a valid unlock.
 *
 * @param {string | null} value - The unlock cookie or header value.
 */
export const devToolsUnlocked = async (value?: string | null) => {
  if (process.env.NEXT_PUBLIC_ENV === 'production') return false;
  if (process.env.NODE_ENV === 'development') return true;
  return verifyDevToolsUnlock(value, devToolsSecret());
};

export const LATENCIES = [
  { id: 0, label: 'Sin demora' },
  { id: 1000, label: '1 s' },
  { id: 3000, label: '3 s' },
  { id: 10000, label: '10 s' },
] as const;

export const FAILURES = [
  { id: 'none', label: 'Ninguno' },
  { id: '500', label: 'Error del servidor (500)' },
  { id: '401', label: 'Sesión vencida (401)' },
] as const;

/** Settings every app's mock routes honor; each app extends it with its own scenario fields. */
export type BaseMockConfig = {
  latency: number;
  failure: (typeof FAILURES)[number]['id'];
};

export const BASE_MOCK_CONFIG: BaseMockConfig = { latency: 0, failure: 'none' };

/**
 * Mock config from the cookie or header value; anything missing or unreadable falls back to the defaults.
 *
 * @param {string | null} value - Cookie or header value, as written by serializeMockConfig.
 * @param {C} defaults - The app's full default config.
 * @param {(config: C) => C} sanitize - Drops values an older cookie may hold that no longer exist.
 */
export const parseMockConfig = <C extends BaseMockConfig>(
  value: string | null | undefined,
  defaults: C,
  sanitize: (config: C) => C = (config) => config,
): C => {
  if (!value) return defaults;
  try {
    return sanitize({ ...defaults, ...JSON.parse(decodeURIComponent(value)) });
  } catch {
    return defaults;
  }
};

export const serializeMockConfig = (config: BaseMockConfig) => encodeURIComponent(JSON.stringify(config));

/** Keeps only the ids that still exist, in list order. */
export const knownIds = <Id extends string>(all: readonly { id: Id }[], picked: unknown): Id[] =>
  all.map(({ id }) => id).filter((id) => Array.isArray(picked) && picked.includes(id));

/**
 * Mock route response shaped by the dev tools config forwarded in the request: delay, then a forced failure or the
 * built body.
 *
 * @param {Request} request - Incoming mock request, carrying the config header.
 * @param {(config: C) => unknown} build - Response body for the config.
 * @param {(value?: string | null) => C} parse - The app's config parser.
 */
export const mockRoute = async <C extends BaseMockConfig>(
  request: Request,
  build: (config: C) => unknown,
  parse: (value?: string | null) => C,
) => {
  const config = parse(request.headers.get(MOCK_CONFIG_HEADER));
  if (config.latency) await new Promise((r) => setTimeout(r, config.latency));
  if (config.failure === '500') return Response.json({ status: 500, message: 'MOCK_ERROR' }, { status: 500 });
  if (config.failure === '401') return Response.json({ code: 'UNAUTHORIZED' }, { status: 401 });
  return Response.json(await build(config));
};
